import { memo, useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type KeyboardEvent } from "react";
import { useStore } from "../store/globalState";
import { useSessionStore } from "../store/useSessionStore";
import { useRouteWithToken } from "../hooks/useRouteWithToken";
import { Trans, useTranslation } from "react-i18next";
import { BodyCopy } from "../components/BodyCopy";
import {
  BankIcon,
  Button,
  CashSpecialIcon,
  ExchangeIcon,
  Layout,
  SupportIcon,
  Title,
  WalletIcon,
  WarningIcon,
} from "lola-framework-ui-test";
import { AuraLayout } from "../components/AuraLayout";
import {
  bankAccountRowLabel,
  looksLikePayerCode,
} from "../utils/bankAccountLabel";
import { APP_ROUTES, TITLE_COLOR } from "../constants";
import { shouldPromptPhone } from "../utils/phoneGate";
import { ensureSmsConsent } from "../components/notifications/smsConsent";
import { RequireActiveClerkSession } from "../hooks/useRequireActiveClerkSession";
import { FlagCircle } from "../components/FlagCircle";
import { QuoteInfo } from "../components/QuoteInfo";
import { amountFormatter } from "../utils";
import { PayoutInfo } from "../components/PayoutInfo";
import { Popup } from "../components/Popup";
import {
  SelectPaymentMethodSlot,
  categoryFromPayment,
  type FlatPayoutPick,
} from "../components/SelectPaymentMethodSlot";
import {
  CashPickupDetailsSlot,
  type CashPickupSelection,
} from "../components/CashPickupDetailsSlot";
import {
  BankAccountDetailsSlot,
  type BankAccountSelection,
} from "../components/BankAccountDetailsSlot";
import { RouteUnavailablePage } from "./RouteUnavailablePage";
import { paymentType } from "../interfaces/payment";
import type { IBankAccountPaymentMethod } from "../interfaces/payment-method";
import masterCardLogo from "../assets/master_card_icon.svg";
import visaLogo from "../assets/visa_color_icon.svg";
import { usePayoutClaimPricing } from "../hooks/usePayoutClaimPricing";
import { usePaymentMethods } from "../hooks/usePaymentMethods";
import { useBankAccounts } from "../hooks/useBankAccounts";
import { useCatalogPayers } from "../hooks/useCatalog";
import { offerableSavedBankAccounts } from "../utils/offerableBankAccounts";
import { payerCodesEquivalent } from "../utils/payerCode";
import { addBankAccount } from "../services/api";
import {
  preparePayoutRail,
  sanitizePayoutAfterPrepare,
} from "../utils/prepare-payout-rail";
import { isPayoutCardValidationStale } from "../utils/pre-validate-payout-card";
import type { DestinationPayoutInput } from "../interfaces/payout-instrument";
import { useClaimableGuard } from "../hooks/useClaimableGuard";
import { getEvents, safeCapture } from "../analytics";
import { quoteMatchesSelection } from "../utils/quoteMatchesSelection";
import { isInitialTransferPaintReady } from "../utils/initialTransferPaint";
import { useQuoteLoadingTimedOut } from "../hooks/useQuoteLoadingTimedOut";
import { useSupportSheet } from "../hooks/useSupportSheet";
import { ScrollFadeIndicator } from "../components/ScrollFadeIndicator";
import { Navbar } from "../components/Navbar";


type PayoutStep = "method" | "cashDetails" | "bankDetails";

/** Title-case each word: "juan CARLOS" → "Juan Carlos". */
const toTitleCase = (value: string): string =>
  value
    .trim()
    .toLowerCase()
    .replace(
      /[^\s-]+/gu,
      (word) => word.charAt(0).toUpperCase() + word.slice(1)
    );

/**
 * `primaryMesh` is usually a gradient string, which is invalid for `color` and
 * `border-color`. The outlined icon in the rate pill needs a solid value, so it
 * falls back to the gradient's first stop.
 */
const solidStop = (color: string) =>
  color.match(/#[0-9a-f]{3,8}|rgba?\([^)]*\)/i)?.[0] ?? color;

export const TransferPage = memo(() => (
  <RequireActiveClerkSession resume={{ kind: "transfer" }} requireOccupant>
    <TransferPageContent />
  </RequireActiveClerkSession>
));

const TransferPageContent = memo(() => {
  useClaimableGuard();
  const { t, i18n } = useTranslation();

  useEffect(() => {
    safeCapture(getEvents().SCREEN_VIEW("route_amount"));
    safeCapture(getEvents().SCREEN_VIEW("summary"));
  }, []);

  const {
    theme,
    paymentState,
    setQuoteState,
    setPaymentState,
    payoutInstrument,
    setPayoutInstrument,
  } = useStore();
  const { session } = useSessionStore();
  const colorConfig = theme.whiteView;
  const {
    backgroundBtn,
    textColorBtn,
    title,
    bodyCopy,
    dropzoneColors,
    bannerHighlightBackground,
    bannerHighlightColor,
    bannerHighlightIconColors,
  } = colorConfig as typeof colorConfig & {
    bannerHighlightBackground?: string;
    bannerHighlightColor?: string;
    bannerHighlightIconColors?: string[];
  };
  const { redirectTo } = useRouteWithToken();
  const { openSupport, supportSheet } = useSupportSheet();
  const [showPaymentPopup, setShowPaymentPopup] = useState(false);
  const [showExcratePopup, setShowExcratePopup] = useState(false);
  const [payoutStep, setPayoutStep] = useState<PayoutStep>("method");
  const [showRouteUnavailable, setShowRouteUnavailable] = useState(false);
  const [shakePayment, setShakePayment] = useState(false);
  const [recipientFirstName, setRecipientFirstName] = useState("");
  const [recipientLastName, setRecipientLastName] = useState("");
  const [bindLoading, setBindLoading] = useState(false);
  const [prepareError, setPrepareError] = useState<string | null>(null);

  const {
    sourceCountry,
    destinationCountry,
    sourceCurrency,
    destinationCurrency,
    fundingMethod,
    options,
    preQuoteLoading,
    preQuoteError,
    capabilityKey,
    quote,
    quoteLoading,
    quoteError,
    refetchQuote,
    routeUnavailable,
    economics,
    recipientHint,
    sendAmount,
    selectedPayoutMethod,
    availableCategories,
  } = usePayoutClaimPricing({ enableQuote: !bindLoading });

  // Destination-card dry-run is corridor-scoped: clear it when the route changes.
  useEffect(() => {
    const payout = useStore.getState().payoutInstrument;
    if (!payout || payout.source !== "new") return;
    if (!payout.cvsValidationId && !payout.validatedFor) return;
    if (
      !isPayoutCardValidationStale(
        payout.validatedFor,
        sourceCountry,
        destinationCountry
      )
    ) {
      return;
    }
    setPayoutInstrument({
      ...payout,
      cvsValidationId: undefined,
      numberToken: undefined,
      validatedFor: undefined,
    });
  }, [sourceCountry, destinationCountry, setPayoutInstrument]);

  const pricingAmount = sendAmount;

  const hintFirst = recipientHint?.firstName?.trim() ?? "";
  const hintLast = recipientHint?.lastName?.trim() ?? "";

  useEffect(() => {
    if (hintFirst && !recipientFirstName) {
      const next = toTitleCase(hintFirst);
      setRecipientFirstName((prev) => (prev === next ? prev : next));
    }
    if (hintLast && !recipientLastName) {
      const next = toTitleCase(hintLast);
      setRecipientLastName((prev) => (prev === next ? prev : next));
    }
  }, [hintFirst, hintLast, recipientFirstName, recipientLastName]);

  const sourceAmountPrefix = sourceCurrency === "USD" ? "$" : "";
  const destinationAmountPrefix = destinationCurrency === "USD" ? "$" : "";

  const { cards: destinationCards, loading: destinationCardsLoading } =
    usePaymentMethods(session.data?.linkId);

  const { accounts: vaultAccounts, loading: vaultAccountsLoading } =
    useBankAccounts(destinationCountry ?? "");

  const bankAvailable = availableCategories.has("BANK");
  const bankMethodOptions = useMemo(
    () => options.filter((o) => o.payoutMethod === "BANK_ACCOUNT"),
    [options]
  );
  const { data: bankPayers, loading: bankPayersLoading } = useCatalogPayers(
    bankAvailable && destinationCountry
      ? {
          ...(sourceCountry ? { originCountryCode: sourceCountry } : {}),
          destinationCountryCode: destinationCountry,
          payoutMethod: "BANK_ACCOUNT",
          ...(fundingMethod ? { fundingMethod } : {}),
        }
      : null,
    bankAvailable
  );
  const catalogBankPayerCodes = useMemo(() => {
    if (!bankPayers) return null;
    return bankPayers
      .filter((p) => p.effectiveStatus === "AVAILABLE")
      .map((p) => p.payerCode);
  }, [bankPayers]);
  const offerableAccounts = useMemo(
    () =>
      offerableSavedBankAccounts(
        vaultAccounts,
        bankMethodOptions,
        catalogBankPayerCodes
      ),
    [vaultAccounts, bankMethodOptions, catalogBankPayerCodes]
  );
  const needsBankCatalog =
    bankAvailable &&
    bankMethodOptions.length > 0 &&
    !bankMethodOptions.some((o) => o.payerCode);
  const accountsLoading =
    bankAvailable &&
    (vaultAccountsLoading ||
      (needsBankCatalog && (bankPayersLoading || catalogBankPayerCodes == null)));
  const payoutPickerLoading =
    preQuoteLoading ||
    (availableCategories.has("CARD") && destinationCardsLoading) ||
    accountsLoading ||
    (availableCategories.size === 0 && !routeUnavailable);
  const bankLabels = useMemo(() => {
    const map: Record<string, string> = {};
    for (const payer of bankPayers ?? []) {
      if (payer.payerCode) {
        map[payer.payerCode.toUpperCase()] = payer.displayName;
      }
    }
    return map;
  }, [bankPayers]);

  useEffect(() => {
    if (!routeUnavailable) return;
    setShowRouteUnavailable((prev) => (prev ? prev : true));
  }, [routeUnavailable]);

  // Incomplete bank/cash still need a details step. Saved bank instruments
  // one-tap from the first popup (`bankSelectionReady`).

  const pricingLoading = preQuoteLoading || quoteLoading;
  const hasPayoutSelection = !!paymentState.payment;
  const quoteLoadingTimedOut = useQuoteLoadingTimedOut(quoteLoading);
  const initialPreQuoteTimedOut = useQuoteLoadingTimedOut(preQuoteLoading);
  const quoteFailed = Boolean(quoteError) || quoteLoadingTimedOut;
  const skuMissing =
    !preQuoteLoading && !!paymentState.payment && !capabilityKey;

  const cardSelectionReady =
    paymentState.payment?.type !== paymentType.CARD ||
    Boolean(paymentState.payment.cardId) ||
    Boolean(
      payoutInstrument?.source === "new" &&
        payoutInstrument.cvsValidationId &&
        payoutInstrument.numberToken
    );

  const cashSelectionReady =
    paymentState.payment?.type !== paymentType.CASH ||
    !!paymentState.payment.cashPickup?.payerId;

  const bankSelectionReady =
    paymentState.payment?.type !== paymentType.BANK ||
    Boolean(
      paymentState.payment.bankAccount?.paymentMethodId ||
        (paymentState.payment.bankAccount?.bankId &&
          paymentState.payment.bankAccount?.accountNumber)
    );

  const payoutReady =
    hasPayoutSelection &&
    cardSelectionReady &&
    cashSelectionReady &&
    bankSelectionReady;
  const quoteMatches = quoteMatchesSelection(quote, quoteLoading, {
    payoutMethod: selectedPayoutMethod,
    capabilityKey,
  });
  const blockForPricing =
    Boolean(pricingAmount) &&
    payoutReady &&
    !quoteMatches &&
    !quoteFailed &&
    !skuMissing;
  const showQuoteLoading =
    (quoteLoading || blockForPricing) && !quoteFailed && !skuMissing;
  // Deferred parity: rainbow border while payout is the incomplete step.
  const payoutPending = !!pricingAmount && !payoutReady;

  const canConfirm =
    !!pricingAmount &&
    payoutReady &&
    quoteMatches &&
    !!recipientFirstName.trim() &&
    !!recipientLastName.trim() &&
    !routeUnavailable;
  const showRainbow = canConfirm || bindLoading;

  const openMethodPicker = useCallback(() => {
    // Incomplete bank/cash details → go straight to that step. Cards live on
    // the first popup, so an incomplete CARD selection reopens the mixed list.
    if (
      paymentState.payment?.type === paymentType.BANK &&
      !bankSelectionReady
    ) {
      setPayoutStep("bankDetails");
    } else if (
      paymentState.payment?.type === paymentType.CASH &&
      !cashSelectionReady
    ) {
      setPayoutStep("cashDetails");
    } else {
      setPayoutStep("method");
    }
    setShowPaymentPopup(true);
  }, [paymentState.payment?.type, bankSelectionReady, cashSelectionReady]);

  const closePaymentPopup = useCallback(() => {
    setShowPaymentPopup(false);
    setPayoutStep("method");
  }, []);

  const handleSelectCash = useCallback(() => {
    safeCapture(getEvents().PAYOUT_SELECTED, { payout_method: "CASH_PICKUP" });
    setPayoutStep("cashDetails");
  }, []);

  const handleSelectBank = useCallback(() => {
    safeCapture(getEvents().PAYOUT_SELECTED, { payout_method: "BANK_ACCOUNT" });
    setPayoutStep("bankDetails");
  }, []);

  const handleSelectOther = useCallback(() => {
    safeCapture(getEvents().PAYOUT_SELECTED, { payout_method: "OTHER" });
    setPayoutInstrument(null);
    setPaymentState({
      payment: {
        cardId: null,
        type: paymentType.OTHER,
        brand: t("payment.other"),
      },
    });
    closePaymentPopup();
  }, [setPaymentState, setPayoutInstrument, t, closePaymentPopup]);

  const handleSelectCardInstrument = useCallback(
    (selection: {
      paymentMethodId: string;
      brand: string;
      last4: string;
    }) => {
      const displayName = `${(selection.brand || "").toUpperCase()}-${selection.last4}`;
      setPayoutInstrument({
        source: "saved",
        paymentMethodId: selection.paymentMethodId,
        brand: selection.brand,
        last4: selection.last4,
      });
      setPaymentState({
        payment: {
          cardId: selection.paymentMethodId,
          type: paymentType.CARD,
          brand: displayName,
        },
      });
      safeCapture(getEvents().PAYOUT_SELECTED, {
        payout_method: "PUSH_TO_CARD",
      });
      safeCapture(getEvents().CARD_REGISTERED, {
        success: true,
        card_brand: selection.brand,
        funding_payment_method_id: selection.paymentMethodId,
        last4: selection.last4,
      });
      closePaymentPopup();
    },
    [setPayoutInstrument, setPaymentState, closePaymentPopup]
  );

  const handleSelectBankInstrument = useCallback(
    (account: IBankAccountPaymentMethod) => {
      const bankCode = (account.bankName || "").toUpperCase();
      const stored = account.bankName?.trim();
      const bankLabel =
        stored && !looksLikePayerCode(stored)
          ? stored
          : bankLabels[bankCode];
      const last4 = account.maskedAccountNumber.replace(/\D/g, "").slice(-4);
      const option =
        bankMethodOptions.find((o) =>
          payerCodesEquivalent(o.payerCode, bankCode)
        ) ??
        bankMethodOptions[0] ??
        null;
      setPayoutInstrument(null);
      setPaymentState({
        payment: {
          cardId: null,
          type: paymentType.BANK,
          brand:
            bankAccountRowLabel({
              stored: bankLabel,
              accountOrLast4: last4,
              unnamedWithDigits: t("transfer.bankAccount", { last4 }),
              empty: bankLabel || t("payment.bankAccount"),
            }),
          bankAccount: {
            source: "saved",
            paymentMethodId: account.paymentMethodId,
            bankId: bankCode,
            bankName: bankLabel || stored,
            last4: last4 || undefined,
          },
        },
      });
      safeCapture(getEvents().PAYOUT_SELECTED, {
        payout_method: "BANK_ACCOUNT",
      });
      if (bankCode) {
        safeCapture(getEvents().CAPABILITY_SELECTED, {
          payer_code: bankCode,
          payout_method: "BANK_ACCOUNT",
          ...(option?.capabilityKey
            ? { capability_key: option.capabilityKey }
            : {}),
        });
      }
      closePaymentPopup();
    },
    [
      bankLabels,
      bankMethodOptions,
      t,
      setPayoutInstrument,
      setPaymentState,
      closePaymentPopup,
    ]
  );

  const handlePayoutPick = useCallback(
    (pick: FlatPayoutPick) => {
      if (pick.kind === "card") {
        handleSelectCardInstrument(pick.card);
        return;
      }
      if (pick.kind === "bank") {
        handleSelectBankInstrument(pick.account);
        return;
      }
      if (pick.kind === "add") {
        safeCapture(getEvents().PAYOUT_SELECTED, {
          payout_method: "PUSH_TO_CARD",
        });
        redirectTo(APP_ROUTES.ADD_CARD_PAGE);
        return;
      }
      if (pick.kind === "addBank") {
        handleSelectBank();
        return;
      }
      if (pick.category === "CASH") {
        handleSelectCash();
        return;
      }
      handleSelectOther();
    },
    [
      handleSelectCardInstrument,
      handleSelectBankInstrument,
      handleSelectCash,
      handleSelectBank,
      handleSelectOther,
      redirectTo,
    ]
  );

  const handleCashDetailsSubmit = useCallback(
    (result: CashPickupSelection) => {
      setPayoutInstrument(null);
      setPaymentState({
        payment: {
          cardId: null,
          type: paymentType.CASH,
          brand: t("transfer.cashPickup", { payer: result.payerName }),
          cashPickup: {
            stateCode: result.stateCode,
            payerId: result.payerId,
            payerName: result.payerName,
          },
        },
      });
      safeCapture(getEvents().CAPABILITY_SELECTED, {
        payer_code: result.payerId,
        payout_method: "CASH_PICKUP",
      });
      closePaymentPopup();
    },
    [
      setPaymentState,
      setPayoutInstrument,
      t,
      closePaymentPopup,
    ]
  );

  const handleBankDetailsSubmit = useCallback(
    (result: BankAccountSelection) => {
      const tail = result.last4 || result.accountNumber?.slice(-4) || "";
      const brand = bankAccountRowLabel({
        stored: result.bankName,
        accountOrLast4: tail || result.accountNumber,
        unnamedWithDigits: t("transfer.bankAccount", { last4: tail }),
        empty: result.bankName || t("payment.bankAccount"),
      });
      setPayoutInstrument(null);
      setPaymentState({
        payment: {
          cardId: null,
          type: paymentType.BANK,
          brand,
          bankAccount: {
            source: result.source,
            bankId: result.bankId,
            bankName: result.bankName,
            accountNumber: result.accountNumber,
            paymentMethodId: result.paymentMethodId,
            last4: result.last4 || tail || undefined,
          },
        },
      });
      if (result.bankId) {
        safeCapture(getEvents().CAPABILITY_SELECTED, {
          payer_code: result.bankId,
          payout_method: "BANK_ACCOUNT",
          ...(result.option?.capabilityKey
            ? { capability_key: result.option.capabilityKey }
            : {}),
        });
      }
      closePaymentPopup();
    },
    [setPaymentState, setPayoutInstrument, t, closePaymentPopup]
  );

  const prepareErrorMessage = useCallback(
    (code?: string) => {
      switch (code) {
        case "PROVISION_FAILED":
          return t("transfer.prepare.errorProvision");
        case "REGISTER_FAILED":
          return t("transfer.prepare.errorRegister");
        case "READINESS_FAILED":
          return t("transfer.prepare.errorReadiness");
        case "CARD_PUSH_STALE":
          return t("payout.card.corridorChanged");
        default:
          return t("transfer.prepare.errorGeneric");
      }
    },
    [t]
  );

  const resolvePayoutInstrument = useCallback((): DestinationPayoutInput | null => {
    if (payoutInstrument?.source === "new" && payoutInstrument.cvsValidationId) {
      return payoutInstrument;
    }
    if (
      payoutInstrument?.source === "saved" &&
      payoutInstrument.paymentMethodId
    ) {
      return payoutInstrument;
    }
    if (
      paymentState.payment?.type === paymentType.CARD &&
      paymentState.payment.cardId
    ) {
      return {
        source: "saved",
        paymentMethodId: paymentState.payment.cardId,
        brand: paymentState.payment.brand,
      };
    }
    return null;
  }, [payoutInstrument, paymentState.payment]);

  const handleConfirmClick = useCallback(async () => {
    if (!payoutReady) {
      setShakePayment(true);
      setTimeout(() => setShakePayment(false), 500);
      return;
    }
    if (
      !pricingAmount ||
      !quoteMatches ||
      !quote ||
      routeUnavailable ||
      !recipientFirstName.trim() ||
      !recipientLastName.trim()
    ) {
      return;
    }

    const linkId = session.data?.linkId;
    if (!linkId) {
      console.error("[TransferPage] No linkId found in session");
      return;
    }

    setQuoteState(quote);
    setPrepareError(null);
    safeCapture(getEvents().CONTINUE_CLICK);

    try {
      setBindLoading(true);

      if (
        paymentState.payment?.type === paymentType.CARD &&
        capabilityKey
      ) {
        const payout = resolvePayoutInstrument();
        if (!payout) {
          setPrepareError(t("transfer.prepare.errorGeneric"));
          return;
        }
        if (
          !payout.paymentMethodId &&
          (isPayoutCardValidationStale(
            payout.validatedFor,
            sourceCountry,
            destinationCountry
          ) ||
            !payout.cvsValidationId)
        ) {
          setPrepareError(prepareErrorMessage("CARD_PUSH_STALE"));
          return;
        }

        const payoutResult = await preparePayoutRail({
          linkId,
          payout,
          capabilityKey,
          cardholderName: payout.cardholderName,
        });

        if (payoutResult.status === "failed") {
          safeCapture(getEvents().CARD_REGISTERED, {
            success: false,
            error_code: payoutResult.errorCode ?? "PROVISION_FAILED",
          });
          setPrepareError(prepareErrorMessage(payoutResult.errorCode));
          return;
        }

        const sanitized = sanitizePayoutAfterPrepare(payoutResult.payout);
        setPayoutInstrument(sanitized);

        setPaymentState({
          payment: {
            cardId: sanitized.paymentMethodId ?? null,
            type: paymentType.CARD,
            brand: `${(sanitized.brand || "").toUpperCase()}-${sanitized.last4 || ""}`,
          },
        });
        safeCapture(getEvents().CARD_REGISTERED, {
          success: true,
          card_brand: sanitized.brand,
          funding_payment_method_id: sanitized.paymentMethodId,
          last4: sanitized.last4,
        });
      }

      if (paymentState.payment?.type === paymentType.BANK) {
        const bank = paymentState.payment.bankAccount;
        if (!bank) {
          setPrepareError(t("transfer.prepare.errorGeneric"));
          return;
        }
        if (!(bank.source === "saved" && bank.paymentMethodId)) {
          if (bank.source === "new" && bank.accountNumber && bank.bankId) {
            try {
              const registered = await addBankAccount(linkId, {
                countryCode: (destinationCountry || "").toUpperCase(),
                bankName: bank.bankId,
                accountNumber: bank.accountNumber,
              });
              setPaymentState({
                payment: {
                  ...paymentState.payment,
                  bankAccount: {
                    ...bank,
                    source: "saved",
                    paymentMethodId: registered.paymentMethodId,
                    last4:
                      bank.last4 ||
                      registered.maskedAccountNumber.replace(/\D/g, "").slice(-4),
                  },
                },
              });
            } catch (regErr) {
              console.error("[TransferPage] Bank register failed:", regErr);
              setPrepareError(t("transfer.prepare.errorRegisterBank"));
              return;
            }
          } else {
            setPrepareError(t("transfer.prepare.errorGeneric"));
            return;
          }
        }
      }

      const payoutMethodLabel =
        paymentState.payment?.type === paymentType.CARD
          ? "PUSH_TO_CARD"
          : paymentState.payment?.type === paymentType.CASH
            ? "CASH_PICKUP"
            : paymentState.payment?.type === paymentType.BANK
              ? "BANK_ACCOUNT"
              : undefined;

      if (capabilityKey) {
        safeCapture(getEvents().CAPABILITY_SELECTED, {
          capability_key: capabilityKey,
          payout_method: payoutMethodLabel,
        });
      }

      const prompt = shouldPromptPhone(session.data, linkId);
      if (prompt) {
        redirectTo(APP_ROUTES.PHONE_PAGE, {
          replace: true,
          state: { next: APP_ROUTES.SENDING_MONEY_PAGE },
        });
        return;
      }
      await ensureSmsConsent(linkId, i18n.language?.slice(0, 2) || "en");
      redirectTo(APP_ROUTES.SENDING_MONEY_PAGE, {
        replace: true,
        state: { next: APP_ROUTES.SENDING_MONEY_PAGE },
      });
    } catch (err) {
      console.error("[TransferPage] Prepare failed:", err);
      setPrepareError(t("transfer.prepare.errorGeneric"));
    } finally {
      setBindLoading(false);
    }
  }, [
    payoutReady,
    paymentState.payment,
    pricingAmount,
    quote,
    quoteMatches,
    capabilityKey,
    routeUnavailable,
    session.data,
    setQuoteState,
    resolvePayoutInstrument,
    prepareErrorMessage,
    setPayoutInstrument,
    setPaymentState,
    redirectTo,
    t,
    i18n,
    recipientFirstName,
    recipientLastName,
    sourceCountry,
    destinationCountry,
  ]);

  const exchangeRate = quote?.exchangeRate ?? economics?.exchangeRate ?? 0;
  const rateText = t("exchange.rate", {
    from: sourceCurrency,
    rate: pricingLoading ? "..." : exchangeRate.toFixed(4),
    to: destinationCurrency,
  });
  const openFxDisclaimer = useCallback(() => setShowExcratePopup(true), []);
  const onFxDisclaimerKeyDown = useCallback((event: KeyboardEvent) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      setShowExcratePopup(true);
    }
  }, []);

  const amountSent = useMemo(() => {
    if (hasPayoutSelection && quoteLoading) return "...";
    if (quote?.sendAmount != null) {
      return amountFormatter(quote.sendAmount, sourceCurrency, {
        country: "en-US",
      });
    }
    if (pricingLoading) return "...";
    if (economics?.sendAmount != null) {
      return amountFormatter(economics.sendAmount, sourceCurrency, {
        country: "en-US",
      });
    }
    if (sendAmount > 0) {
      return amountFormatter(sendAmount, sourceCurrency, { country: "en-US" });
    }
    return "";
  }, [
    hasPayoutSelection,
    quote,
    quoteLoading,
    economics,
    pricingLoading,
    sourceCurrency,
    sendAmount,
  ]);

  const amountToReceive = useMemo(() => {
    if (hasPayoutSelection) {
      if (quoteLoading) return "...";
      if (quote?.receiveAmount != null) {
        return amountFormatter(quote.receiveAmount, destinationCurrency, {
          country: "en-US",
        });
      }
      return "";
    }
    if (pricingLoading) return "...";
    if (economics?.receiveAmount != null) {
      return amountFormatter(economics.receiveAmount, destinationCurrency, {
        country: "en-US",
      });
    }
    return "";
  }, [
    hasPayoutSelection,
    quote,
    quoteLoading,
    economics,
    pricingLoading,
    destinationCurrency,
  ]);

  const receiveSummary = useMemo(() => {
    if (!quote || quoteLoading) return null;
    if (!amountToReceive || amountToReceive === "...") return null;
    return t("payoutClaim.receiveSummary", {
      amount: `${destinationAmountPrefix}${amountToReceive}`,
      currency: destinationCurrency,
    });
  }, [
    quote,
    quoteLoading,
    amountToReceive,
    destinationAmountPrefix,
    destinationCurrency,
    t,
  ]);

  const pickIcon = useCallback((type: paymentType, brand: string) => {
    switch (type) {
      case paymentType.CARD: {
        let finalIcon = null;
        if (brand.toLocaleUpperCase().includes("VISA")) finalIcon = visaLogo;
        if (brand.toLocaleUpperCase().includes("MASTERCARD"))
          finalIcon = masterCardLogo;

        return {
          brand,
          icon: <img src={finalIcon ?? ""} alt={`${brand} logo`} />,
        };
      }
      case paymentType.BANK:
        return {
          brand,
          icon: <BankIcon size={20} />,
        };
      case paymentType.CASH:
        return {
          brand,
          icon: <CashSpecialIcon size={20} />,
        };

      default:
        return {
          brand,
          icon: <WalletIcon size={20} />,
        };
    }
  }, []);

  const paymentSelected = useMemo(() => {
    if (!paymentState.payment) {
      return {
        brand: t("payoutClaim.selectPayoutMethod"),
        icon: <WalletIcon size={20} />,
      };
    }
    if (!cardSelectionReady || !bankSelectionReady || !cashSelectionReady) {
      return {
        brand: t("payoutClaim.selectPayoutMethod"),
        icon: pickIcon(paymentState.payment.type, paymentState.payment.brand)
          .icon,
      };
    }
    return pickIcon(paymentState.payment.type, paymentState.payment.brand);
  }, [
    paymentState.payment,
    cardSelectionReady,
    bankSelectionReady,
    cashSelectionReady,
    pickIcon,
    t,
  ]);

  const initialPricingLoading =
    (preQuoteLoading && !initialPreQuoteTimedOut) ||
    (hasPayoutSelection && quoteLoading && !quoteFailed);
  const initialPaintLatched = useRef(false);
  const initialPaintReady = isInitialTransferPaintReady({
    sessionReady: true,
    amountResolved: true,
    needsPrice: sendAmount > 0,
    pricingLoading: initialPricingLoading,
    pricingSettled:
      routeUnavailable ||
      Boolean(preQuoteError) ||
      quoteFailed ||
      initialPreQuoteTimedOut ||
      (economics?.sendAmount != null && economics.receiveAmount != null),
    methodsLoading: false,
  });
  if (initialPaintReady) initialPaintLatched.current = true;

  if (showRouteUnavailable || routeUnavailable) {
    return (
      <RouteUnavailablePage
        destinationCountry={destinationCountry ?? ""}
        payerCode={paymentState.payment?.cashPickup?.payerId}
        onClose={() => setShowRouteUnavailable(false)}
      />
    );
  }

  if (!initialPaintLatched.current) {
    return (
      <AuraLayout colorConfig={colorConfig}>
        <Layout.Content>
          <div className="landing-section content-center loader-page">
            <section className="content">
              <div
                className="loader-spinner"
                role="status"
                aria-label={t("transfer.loadingQuote", "Loading...")}
              />
            </section>
          </div>
        </Layout.Content>
      </AuraLayout>
    );
  }

  return (
    <>
      <AuraLayout colorConfig={colorConfig} className="scrollable need-help-header">
        <Layout.Header>
          <Navbar
            title={t("common.needHelp")}
            align="right"
            onBackClick={() =>
              redirectTo(APP_ROUTES.TRANSACTIONS_PAGE, { replace: true })
            }
            onIconClick={() => openSupport()}
            icon={<SupportIcon size={35} colors={dropzoneColors} />}
          />
        </Layout.Header>
        <Layout.Content className="transfer-section">
          <ScrollFadeIndicator color={bodyCopy} />
          <section>
            <Title title={t("payoutClaim.receiveTitle")} align="left" color={TITLE_COLOR} />
            <section className="estimated-quote-section stacked-quote">
              <section className="quote-container">
                <div className="banrural-amounts">
                  <div className="banrural-send-wrap">
                    <QuoteInfo
                      amount={amountToReceive || undefined}
                      label={t("payoutClaim.youReceive")}
                      currency={destinationCurrency}
                      textColor={bodyCopy}
                      mode="info"
                      amountPrefix={destinationAmountPrefix}
                      isActive
                      icon={<FlagCircle iso2={destinationCountry.toLowerCase()} />}
                    />
                    <span
                      className="banrural-swap"
                      role="button"
                      tabIndex={0}
                      onClick={openFxDisclaimer}
                      onKeyDown={onFxDisclaimerKeyDown}
                      style={{ background: backgroundBtn }}
                    >
                      <ExchangeIcon size={18} colors={[textColorBtn, textColorBtn]} />
                    </span>
                  </div>
                  <QuoteInfo
                    label={t("payoutClaim.sentFrom")}
                    currency={sourceCurrency}
                    textColor={bodyCopy}
                    mode="info"
                    amount={amountSent || undefined}
                    amountPrefix={sourceAmountPrefix}
                    icon={<FlagCircle iso2={sourceCountry.toLowerCase()} />}
                    bottomSlot={
                      <section
                        className="banrural-exchange-rate banrural-exchange-rate--alert"
                        role="button"
                        tabIndex={0}
                        onClick={openFxDisclaimer}
                        onKeyDown={onFxDisclaimerKeyDown}
                        style={
                          {
                            "--bgc": bannerHighlightBackground ?? bodyCopy,
                            "--colorc": solidStop(
                              bannerHighlightIconColors?.[0] ?? title,
                            ),
                            "--fillc": bannerHighlightColor ?? title,
                          } as CSSProperties
                        }
                      >
                        <span className="banrural-exchange-rate-icon">i</span>
                        <span className="banrural-exchange-rate-text">
                          <b>{rateText}</b>
                          <label>{t("exchange.estimatedRate")}</label>
                        </span>
                      </section>
                    }
                  />
                </div>
              </section>
            </section>
            <section className="payout-container">
              <PayoutInfo
                title={t("payoutClaim.payoutMethod")}
                subtitle={paymentSelected.brand}
                icon={paymentSelected.icon}
                background={backgroundBtn}
                color={textColorBtn}
                onChange={openMethodPicker}
                className={`${payoutPending ? "payment-required" : ""} ${
                  shakePayment ? "shake" : ""
                }`.trim()}
              />
              <section className="recipient-identity-fields">
                <BodyCopy textColor={TITLE_COLOR} className="recipient-identity-label">
                  {t("payoutClaim.yourName")}
                </BodyCopy>
                <div className="recipient-identity-inputs">
                  <input
                    type="text"
                    className="recipient-identity-input"
                    value={[recipientFirstName, recipientLastName]
                      .filter(Boolean)
                      .join(" ")}
                    readOnly
                    placeholder={t("payoutClaim.yourName")}
                    autoComplete="name"
                  />
                </div>
              </section>
              {skuMissing && (
                <BodyCopy className="payment-error-text" role="alert">
                  {t("payoutClaim.payoutMethodUnavailable")}
                </BodyCopy>
              )}
              {quoteFailed && !skuMissing ? (
                <>
                  <BodyCopy className="payment-error-text" role="alert">
                    {t("transfer.quoteError")}
                  </BodyCopy>
                  <Button
                    variant="cancel"
                    size="small"
                    color={bodyCopy}
                    onClick={() => {
                      void refetchQuote();
                    }}
                  >
                    {t("transfer.quoteRetry")}
                  </Button>
                </>
              ) : null}
              {prepareError && (
                <BodyCopy className="payment-error-text" role="alert">
                  {prepareError}
                </BodyCopy>
              )}
              <BodyCopy className="footer-text transfer-timing-note" textColor={bodyCopy}>
                <b>{t("payoutClaim.instantReceive")}</b>
                {" "}
                <span className="transfer-timing-note-rest">
                  {t("payoutClaim.mayTakeFew")}
                </span>
              </BodyCopy>
            </section>
          </section>
        </Layout.Content>
        <Layout.Footer>
          <section className="footer-section">
            {receiveSummary && (
              <section className="footer-summary">
                <BodyCopy className="footer-text">{receiveSummary}</BodyCopy>
              </section>
            )}
            <div className={`button-wrapper ${showRainbow ? "rainbow-ready" : ""}`}>
              <Button
                size="large"
                background={backgroundBtn}
                color={textColorBtn}
                disabled={
                  !pricingAmount ||
                  routeUnavailable ||
                  bindLoading ||
                  blockForPricing ||
                  quoteFailed
                }
                onClick={handleConfirmClick}
              >
                {bindLoading
                  ? t("transfer.verifying")
                  : showQuoteLoading
                    ? t("transfer.loadingQuote")
                    : t("payoutClaim.confirmReceive")}
              </Button>
            </div>
          </section>
        </Layout.Footer>
      </AuraLayout>
      <Popup visible={showPaymentPopup} onClose={closePaymentPopup}>
        {payoutStep === "method" ? (
          <SelectPaymentMethodSlot
            selected={categoryFromPayment(paymentState.payment?.type)}
            selectedPaymentMethodId={
              paymentState.payment?.cardId ??
              paymentState.payment?.bankAccount?.paymentMethodId ??
              null
            }
            cards={destinationCards}
            cardsLoading={destinationCardsLoading}
            accounts={offerableAccounts}
            accountsLoading={accountsLoading}
            loading={payoutPickerLoading}
            bankLabels={bankLabels}
            onPick={handlePayoutPick}
            onClose={closePaymentPopup}
          />
        ) : payoutStep === "bankDetails" ? (
          <BankAccountDetailsSlot
            destinationCountry={destinationCountry}
            destinationCurrency={destinationCurrency}
            fundingMethod={fundingMethod}
            originCountryCode={sourceCountry}
            options={options}
            optionsLoading={preQuoteLoading}
            value={paymentState.payment?.bankAccount}
            onBack={() => setPayoutStep("method")}
            onClose={closePaymentPopup}
            onSubmit={handleBankDetailsSubmit}
          />
        ) : (
          <CashPickupDetailsSlot
            destinationCountry={destinationCountry}
            fundingMethod={fundingMethod}
            originCountryCode={sourceCountry}
            options={options}
            optionsLoading={preQuoteLoading}
            value={paymentState.payment?.cashPickup}
            onBack={() => setPayoutStep("method")}
            onClose={closePaymentPopup}
            onSubmit={handleCashDetailsSubmit}
          />
        )}
      </Popup>
      <Popup
        visible={showExcratePopup}
        onClose={() => setShowExcratePopup(false)}
      >
        <section className="qoute-popup">
          <WarningIcon size={60} />
          <BodyCopy textColor={bodyCopy}>
            <Trans i18nKey="transfer.quote.disclaimer" />
          </BodyCopy>
        </section>
      </Popup>
      {supportSheet}
    </>
  );
});
